import type { Page } from '../../app/feature';
import type { AuthRole, PanelUser } from '../../shared/types';
import { delegate, esc, inputValue, q } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { dateTime } from '../../shared/format';
import { ApiError } from '../../shared/api/http';
import { currentUser } from '../../shared/session/session';
import { toast } from '../../shared/ui/toast';
import { copy } from '../../shared/ui/clipboard';
import { usersApi } from './users.api';
import { getState } from '../../shared/store/store';
import { bus } from '../../shared/events';

let users: PanelUser[] = [];
let loading = false;

const roleKey: Record<AuthRole, string> = { ADMIN: 'role.admin', HOST: 'role.host', SUPPORT: 'role.support' };
const roleChip: Record<AuthRole, string> = { ADMIN: 'chip-orange', HOST: 'chip-gold', SUPPORT: 'chip-dim' };

/**
 * Acessos ao painel: o admin cadastra outros admins, influenciadores e suporte,
 * com login por e-mail e senha. Sem senha informada, a API gera uma temporária,
 * mostrada uma única vez para ser enviada à pessoa.
 */
export const usersView: Page = {
  id: 'accesos',
  pattern: '/accesos',
  roles: ['ADMIN'],
  nav: { key: 'nav.users', icon: '🔑', group: 'config' },

  template: () => `
<div>
  <div class="view-head">
    <div>
      <h2 data-i18n="users.title">Accesos al panel</h2>
      <p data-i18n="users.sub">Cada persona entra con su e-mail y contraseña. El administrador ve todo; el influencer, sólo el estudio de su sorteo.</p>
    </div>
  </div>

  <div class="cols">
  <div class="span-8">
  <div class="panel-head">
    <h3 data-i18n="users.list">Personas con acceso</h3>
    <div class="spacer"></div>
    <select id="u-filter" style="max-width:200px">
      <option value="" data-i18n="users.all">Todos los roles</option>
      <option value="HOST" data-i18n="role.host">Influencer</option>
      <option value="ADMIN" data-i18n="role.admin">Administración</option>
      <option value="SUPPORT" data-i18n="role.support">Soporte</option>
    </select>
  </div>
  <div class="tw">
    <table id="tbl-users">
      <thead><tr>
        <th data-i18n="th.person">Persona</th>
        <th data-i18n="users.role">Rol</th>
        <th data-i18n="ev.assigned">Sorteo asignado</th>
        <th data-i18n="th.status">Estado</th>
        <th></th>
      </tr></thead>
      <tbody></tbody>
    </table>
  </div>
  </div>

  <aside class="span-4 stack">
  <div class="panel" id="u-created" hidden style="border-color: var(--green-2)">
    <div class="panel-head"><h3 data-i18n="users.createdTitle">Acceso creado</h3><div class="spacer"></div><button class="btn btn-sm btn-ghost" id="u-created-close">✕</button></div>
    <div class="stack">
      <p class="hint" data-i18n="users.createdHint">Copia y envía estos datos ahora: la contraseña temporal no vuelve a mostrarse.</p>
      <div class="inline" style="gap:10px; flex-wrap:wrap">
        <span class="code-pill" id="u-created-email">—</span>
        <span class="code-pill" id="u-created-pass">—</span>
      </div>
      <button class="btn btn-sm btn-primary" id="u-created-copy" data-i18n="users.copyCreds">Copiar datos</button>
    </div>
  </div>

  <div class="panel">
    <div class="panel-head"><h3 data-i18n="users.new">Nuevo acceso</h3></div>
    <form id="user-form" autocomplete="off" novalidate>
      <div class="form-grid">
        <div class="field full"><label for="u-name" data-i18n="f.name">Nombre</label><input type="text" id="u-name" placeholder="Luna Riquelme" required></div>
        <div class="field full"><label for="u-email" data-i18n="auth.email">E-mail</label><input type="email" id="u-email" placeholder="luna@apuesterapido.com" required></div>
        <div class="field full"><label for="u-role" data-i18n="users.role">Rol</label>
          <select id="u-role">
            <option value="HOST" data-i18n="role.host">Influencer</option>
            <option value="ADMIN" data-i18n="role.admin">Administración</option>
            <option value="SUPPORT" data-i18n="role.support">Soporte</option>
          </select>
        </div>
        <div class="field" id="u-handle-field"><label for="u-handle" data-i18n="ev.handle">Usuario / canal</label><input type="text" id="u-handle" placeholder="@lunaplay"></div>
        <div class="field" id="u-event-field"><label for="u-event" data-i18n="ev.assigned">Sorteo asignado</label><select id="u-event"></select></div>
        <div class="field full"><label for="u-password" data-i18n="users.password">Contraseña (opcional)</label><input type="text" id="u-password" autocomplete="new-password" placeholder="" minlength="8"></div>
        <p class="hint full" data-i18n="users.passwordHint">Si dejas la contraseña vacía, el sistema genera una temporal y te la muestra una sola vez.</p>
        <div class="field full"><button type="submit" class="btn btn-primary" id="u-submit" data-i18n="users.create">Crear acceso</button></div>
      </div>
      <div class="auth-error" id="u-error" role="alert"></div>
    </form>
  </div>
  </aside>
  </div>
</div>`,

  mount() {
    const form = q<HTMLFormElement>('#user-form');
    const roleSel = q<HTMLSelectElement>('#u-role');
    const syncHandle = (): void => {
      const host = roleSel.value === 'HOST';
      q('#u-handle-field').hidden = !host;
      q('#u-event-field').hidden = !host;
    };
    bus.on('state', renderEventOptions);
    roleSel.onchange = syncHandle; syncHandle();

    form.addEventListener('submit', async e => {
      e.preventDefault();
      const error = q('#u-error'); error.textContent = '';
      const name = inputValue('#u-name'), email = inputValue('#u-email'), password = inputValue('#u-password');
      if (name.length < 2) { error.textContent = t('users.errName'); return; }
      if (!email.includes('@')) { error.textContent = t('auth.errEmail'); return; }
      if (password && password.length < 8) { error.textContent = t('auth.errShort'); return; }
      const role = roleSel.value as AuthRole;
      const submit = q<HTMLButtonElement>('#u-submit'); submit.disabled = true;
      try {
        const created = await usersApi.create({
          name, email, role,
          handle: role === 'HOST' ? inputValue('#u-handle') || undefined : undefined,
          eventId: role === 'HOST' ? q<HTMLSelectElement>('#u-event').value || undefined : undefined,
          password: password || undefined,
        });
        showCreated(created.email, created.temporaryPassword ?? password);
        form.reset(); syncHandle();
        toast(t('msg.saved'));
        await reload();
      } catch (err) {
        error.textContent = err instanceof ApiError ? err.message : t('auth.errGeneric');
      } finally {
        submit.disabled = false;
      }
    });

    q('#u-created-close').onclick = () => { q('#u-created').hidden = true; };
    q('#u-created-copy').onclick = () => copy(`${t('auth.email')}: ${q('#u-created-email').textContent}\n${t('auth.password')}: ${q('#u-created-pass').textContent}`);
    q<HTMLSelectElement>('#u-filter').onchange = renderTable;

    const table = q('#tbl-users');
    delegate(table, 'click', '[data-toggle-active]', async el => {
      const u = users.find(x => x.id === el.dataset.toggleActive); if (!u) return;
      await run(() => usersApi.update(u.id, { active: !u.active }));
    });
    delegate(table, 'click', '[data-newpass]', async el => {
      const u = users.find(x => x.id === el.dataset.newpass); if (!u) return;
      if (!confirm(t('users.confirmNewPass').replace('{name}', u.name))) return;
      const password = generatePassword();
      await run(async () => { await usersApi.update(u.id, { password }); showCreated(u.email, password); });
    });
    table.addEventListener('change', async e => {
      const sel = (e.target as Element).closest<HTMLSelectElement>('select[data-assign]');
      if (!sel) return;
      const u = users.find(x => x.id === sel.dataset.assign); if (!u) return;
      await run(() => usersApi.update(u.id, { eventId: sel.value || null }));
    });
    delegate(table, 'click', '[data-del]', async el => {
      const u = users.find(x => x.id === el.dataset.del); if (!u) return;
      if (!confirm(t('users.confirmDelete').replace('{name}', u.name))) return;
      await run(() => usersApi.remove(u.id));
    });
  },

  render() {
    if (!currentUser() || currentUser()?.role !== 'ADMIN') return;
    renderEventOptions();
    if (!loading) void reload();
    else renderTable();
  }
};

async function reload(): Promise<void> {
  loading = true;
  try { users = await usersApi.list(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
  finally { loading = false; renderTable(); }
}

async function run(action: () => Promise<unknown>): Promise<void> {
  try { await action(); toast(t('msg.saved')); await reload(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
}

function renderTable(): void {
  const filter = q<HTMLSelectElement>('#u-filter').value as AuthRole | '';
  const me = currentUser();
  const rows = users.filter(u => !filter || u.role === filter);
  q('#tbl-users tbody').innerHTML = rows.map(u => `<tr>
    <td><b>${esc(u.name)}</b>${u.handle ? ' <span class="muted">' + esc(u.handle) + '</span>' : ''}<div class="muted" style="font-size:12px">${esc(u.email)}</div><div class="muted" style="font-size:11px">${esc(t('users.lastLogin'))}: ${u.lastLoginAt ? dateTime(Date.parse(u.lastLoginAt)) : '—'}</div></td>
    <td><span class="chip ${roleChip[u.role]}">${esc(t(roleKey[u.role]))}</span></td>
    <td>${u.role === 'HOST' ? eventSelect(u) : '<span class="muted">—</span>'}</td>
    <td><span class="chip ${u.active ? 'chip-green' : 'chip-dim'}">${u.active ? t('ev.on') : t('ev.off')}</span></td>
    <td style="text-align:right; white-space:nowrap">
      <button class="btn btn-sm btn-ghost" data-newpass="${u.id}" title="${esc(t('users.newPass'))}">🔑</button>
      ${u.id === me?.id ? '' : `
      <button class="btn btn-sm btn-ghost" data-toggle-active="${u.id}" title="${u.active ? esc(t('users.block')) : esc(t('users.unblock'))}">${u.active ? '🚫' : '✔'}</button>
      <button class="btn btn-sm btn-ghost" data-del="${u.id}" title="${esc(t('common.del'))}">✕</button>`}
    </td>
  </tr>`).join('') || `<tr><td colspan="5" class="empty">${loading ? '…' : '—'}</td></tr>`;
}

/** Opções de sorteio (do cache local hidratado pela API) para o formulário de novo acesso. */
function renderEventOptions(): void {
  const sel = document.querySelector<HTMLSelectElement>('#u-event');
  if (!sel) return;
  const keep = sel.value;
  sel.innerHTML = `<option value="">${esc(t('ev.unassigned'))}</option>` +
    getState().events.map(e => `<option value="${e.id}">${esc(e.name)}</option>`).join('');
  if (keep) sel.value = keep;
}

function eventSelect(u: PanelUser): string {
  const options = getState().events.map(e =>
    `<option value="${e.id}" ${u.eventId === e.id ? 'selected' : ''}>${esc(e.name)}</option>`).join('');
  return `<select data-assign="${u.id}" style="max-width:220px; padding:5px 8px; font-size:12.5px">
    <option value="" ${u.eventId ? '' : 'selected'}>${esc(t('ev.unassigned'))}</option>${options}</select>`;
}

function showCreated(email: string, password: string | undefined): void {
  q('#u-created-email').textContent = email;
  q('#u-created-pass').textContent = password ?? '••••••••';
  q('#u-created').hidden = false;
  q('#u-created').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/** Senha legível, sem caracteres ambíguos, gerada no navegador para redefinições. */
function generatePassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint8Array(12); crypto.getRandomValues(bytes);
  return Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
}

